import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import { Input } from "@intility/bifrost-react";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import { resultPath, type SearchResult, searchDocs } from "./search";

const MAX_DROPDOWN_RESULTS = 12;

/**
 * Docs search input with a dropdown results panel. Designed for the page
 * header `actions` slot: the input is inline, and results overlay below it
 * as an absolutely-positioned panel so the page content stays visible.
 */
export function DocSearch() {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const results = useMemo(
    () => (query.trim() ? searchDocs(query) : []),
    [query],
  );
  const trimmed = query.trim();
  const showDropdown = open && trimmed.length > 0;

  return (
    <div ref={containerRef} style={{ position: "relative", width: 280 }}>
      <Input
        label="Search docs"
        hideLabel
        placeholder="Search the documentation…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Defer so a click on a result link registers before we close.
          setTimeout(() => setOpen(false), 150);
        }}
        icon={faSearch}
        clearable
        small
      />

      {showDropdown && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            left: 0,
            zIndex: 100,
            marginTop: 4,
            maxHeight: 400,
            overflowY: "auto",
            backgroundColor: "var(--bfc-base-2)",
            border: "1px solid var(--bfc-base-c-dimmed)",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)",
          }}
        >
          {results.length === 0 ? (
            <p
              style={{
                color: "var(--bfc-base-c-2)",
                margin: 0,
                padding: "12px 14px",
                fontSize: "0.9em",
              }}
            >
              No documentation matches &ldquo;{trimmed}&rdquo;.
            </p>
          ) : (
            <ul
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
              }}
            >
              {results.slice(0, MAX_DROPDOWN_RESULTS).map((r) => (
                <DocSearchResultItem
                  key={`${r.sectionKey}/${r.slug}/${r.headingId ?? ""}`}
                  result={r}
                />
              ))}
              {results.length > MAX_DROPDOWN_RESULTS && (
                <li
                  style={{
                    padding: "8px 14px",
                    color: "var(--bfc-base-c-2)",
                    fontSize: "0.85em",
                    borderTop: "1px solid var(--bfc-base-c-dimmed)",
                  }}
                >
                  {results.length - MAX_DROPDOWN_RESULTS} more result
                  {results.length - MAX_DROPDOWN_RESULTS > 1 ? "s" : ""}…
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function DocSearchResultItem({ result }: { result: SearchResult }) {
  return (
    <li
      style={{
        borderBottom: "1px solid var(--bfc-base-c-dimmed)",
      }}
    >
      <Link
        to={resultPath(result)}
        style={{
          display: "block",
          padding: "10px 14px",
          textDecoration: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span style={{ color: "var(--bfc-base-c)" }}>{result.navLabel}</span>
          <span
            style={{
              color: "var(--bfc-base-c-2)",
              fontSize: "0.85em",
            }}
          >
            {result.sectionLabel}
            {result.headingText ? ` \u203A ${result.headingText}` : ""}
          </span>
        </div>
        {result.snippet && (
          <span
            style={{
              display: "block",
              color: "var(--bfc-base-c-2)",
              marginTop: 2,
              fontSize: "0.9em",
            }}
          >
            {result.snippet}
          </span>
        )}
      </Link>
    </li>
  );
}
